import { Vendor } from '../types/vendor';

/**
 * Vendor Helper Functions
 * 
 * These functions normalize data access for Vendor objects to handle
 * inconsistencies in the Vendor type definition where the same data
 * is accessible through multiple property names.
 */

/**
 * Get vendor address - handles both address and physicalAddress properties
 */
export function getVendorAddress(vendor: Vendor): string {
  const addr =
    vendor.physicalAddress ||
    (('address' in vendor && (vendor as any).address) ? (vendor as any).address : undefined);

  if (!addr) return 'N/A';

  const parts = [
    addr.street,
    addr.street2,
    `${addr.city}, ${addr.state} ${addr.zip}`,
  ].filter(Boolean);

  return parts.join(', ');
}

/**
 * Get vendor address object - returns the full address object
 */
export function getVendorAddressObject(vendor: Vendor) {
  return vendor.physicalAddress ||
    (('address' in vendor && (vendor as any).address) ? (vendor as any).address : undefined);
}

/**
 * Get vendor services - handles both servicesOffered and services properties
 */
export function getVendorServices(vendor: Vendor): string[] {
  const servicesOffered =
    'servicesOffered' in vendor && (vendor as any).servicesOffered
      ? (vendor as any).servicesOffered
      : undefined;

  return vendor.services || servicesOffered || [];
}

/**
 * Get jobs completed - handles both completedJobs and performanceMetrics.jobsCompleted
 */
export function getJobsCompleted(vendor: Vendor): number {
  if (vendor.performanceMetrics?.jobsCompleted !== undefined) {
    return vendor.performanceMetrics.jobsCompleted;
  }
  // Safely handle legacy field without TypeScript error
  if ('completedJobs' in vendor && (vendor as any).completedJobs !== undefined) {
    return Number((vendor as any).completedJobs) || 0;
  }
  return 0;
}

/**
 * Get vendor rating - safely extracts rating value
 */
export function getVendorRating(vendor: Vendor): number | undefined {
  if (vendor.rating) {
    return typeof vendor.rating === 'number' ? vendor.rating : vendor.rating.overall;
  }
  return undefined;
}

/**
 * Get total review count - safely extracts review count
 */
export function getReviewCount(vendor: Vendor): number {
  if (vendor.rating && typeof vendor.rating === 'object' && 'totalReviews' in vendor.rating) {
    return vendor.rating.totalReviews || 0;
  }
  return 0;
}

/**
 * Check if vendor has insurance on file
 */
export function hasInsurance(vendor: Vendor): boolean {
  return !!(vendor.generalLiability || vendor.workersCompensation);
}

/**
 * Check if vendor has active licenses
 */
export function hasActiveLicenses(vendor: Vendor): boolean {
  return vendor.licenses && vendor.licenses.length > 0;
}

/**
 * Get vendor contact info as formatted string
 */
export function getVendorContactInfo(vendor: Vendor): {
  name: string;
  title?: string;
  phone: string;
  email: string;
  alternatePhone?: string;
} {
  return {
    name: vendor.contactName,
    title: vendor.contactTitle,
    phone: vendor.phone,
    email: vendor.email,
    alternatePhone: vendor.alternatePhone,
  };
}

/**
 * Calculate profile completeness percentage
 */
export function calculateProfileCompleteness(vendor: Vendor): number {
  if (vendor.profileCompleteness !== undefined) {
    return vendor.profileCompleteness;
  }
  
  // Calculate based on filled fields
  const fields = [
    vendor.name,
    vendor.contactName,
    vendor.email,
    vendor.phone,
    getVendorAddressObject(vendor),
    getVendorServices(vendor).length > 0,
    vendor.paymentTerms,
    vendor.preferredPaymentMethod,
    hasInsurance(vendor),
    hasActiveLicenses(vendor),
  ];
  
  const filledFields = fields.filter(Boolean).length;
  return Math.round((filledFields / fields.length) * 100);
}
